import React from 'react';
import { Icon, IconType } from '@chargify/icons';

import { Group } from './components';
import { IconContainer, Rotate, StyledButton } from './Button.styles';

import { ButtonColor, ButtonFill } from './types';
import { ComponentSize } from '../types';

export type ButtonProps = {
  /**
   * Button contents
   */
  label?: string;
  /**
   * Button color schema
   */
  color?: ButtonColor;
  /**
   * Component size
   */
  size?: ComponentSize;
  /**
   * Component fill
   */
  fill?: ButtonFill;
  /**
   * Component disabled mode
   */
  disabled?: boolean;
  /**
   * Component loading state
   */
  isLoading?: boolean;
  /**
   * Component loading state text
   */
  loadingText?: string;
  /**
   * Component left icon
   */
  iconLeft?: IconType;
  /**
   * Component right icon
   */
  iconRight?: IconType;
  /**
   * Optional click handler
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
} & React.HTMLAttributes<HTMLButtonElement>;

const Button = ({
  label,
  color = 'primary',
  size = 'medium',
  fill = 'solid',
  disabled = false,
  iconLeft,
  iconRight,
  isLoading = false,
  loadingText = 'Loading',
  onClick,
  ...rest
}: ButtonProps) => (
  <StyledButton
    role="button"
    tabIndex={0}
    colorVariant={`${fill}-${color}`}
    color={color}
    size={size}
    fill={fill}
    isLoading={isLoading}
    disabled={disabled}
    hasIconOnly={!label && !!(iconLeft || iconRight)}
    onClick={onClick}
    {...rest}
  >
    {!isLoading && iconLeft && (
      <IconContainer iconSize={size}>
        <Icon fill="currentColor" type={iconLeft} />
      </IconContainer>
    )}
    {isLoading ? loadingText : label}
    {!isLoading && iconRight && (
      <IconContainer iconSize={size}>
        <Icon fill="currentColor" type={iconRight} />
      </IconContainer>
    )}
    {isLoading && (
      <IconContainer iconSize={size}>
        <Rotate>
          <Icon fill="currentColor" type="spinner" />
        </Rotate>
      </IconContainer>
    )}
  </StyledButton>
);

export default Object.assign(Button, {
  Group,
});
